import React, { useState, useEffect } from 'react';
import { useLang } from '../hooks/useLang';
// import { loadStripe } from '@stripe/stripe-js';

// Note: Payment is handled via backend redirect to Stripe Checkout
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentCheckout = ({ farmData, onPaymentSuccess, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [email, setEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { t, lang } = useLang();

  // Check for test mode URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isTestMode = urlParams.get('test') === 'true';
  const isFreeMode = (process.env.REACT_APP_FREE_MODE === 'true') || (urlParams.get('free') === 'true');

  // DEBUG: Log current language and translations
  useEffect(() => {
    console.log('🌍 PaymentCheckout - Current language:', lang);
    console.log('🌍 PaymentCheckout - t(payment.title):', t('payment.title'));
    console.log('🌍 PaymentCheckout - t(payment.subtitle):', t('payment.subtitle'));
  }, [lang, t]);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      setIsMobile(isMobileDevice);
      console.log('📱 Mobile device detected:', isMobileDevice);
    };
    checkMobile();
  }, []);

  const handlePayment = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Debug logging for mobile
    console.log('🔥 Payment button clicked on mobile device');
    console.log('📱 User agent:', navigator.userAgent);
    console.log('📱 Touch event type:', e.type);
    
    // Mobile Safari fix: Ensure button is not disabled
    if (loading) {
      console.log('⚠️ Button already loading, preventing double-click');
      return;
    }
    
    setLoading(true);
    
    try {
      // const stripe = await stripePromise;
      
      // Backend API call to create checkout session
      const apiUrl = process.env.REACT_APP_API_URL || 'https://centeo-backend.onrender.com';
      console.log('🌐 Making API call to:', `${apiUrl}/api/payment/create-checkout-session`);
      console.log('📦 Sending farmData:', farmData);
      
      const response = await fetch(`${apiUrl}/api/payment/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          farmData,
          language: lang, // Send current language to backend
          couponCode: couponCode, // Add coupon code
          amount: 990, // 9.90€ in cents
          currency: 'eur'
        }),
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('📡 Error response body:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const responseText = await response.text();
      console.log('📡 Raw response:', responseText);
      
      let session;
      try {
        session = JSON.parse(responseText);
      } catch (parseError) {
        console.error('📡 JSON Parse Error:', parseError);
        throw new Error(`Invalid JSON response: ${responseText.substring(0, 100)}...`);
      }
      
      console.log('📋 Session response:', session);

      if (session.success) {
        console.log('✅ Redirecting to Stripe URL:', session.url);
        // Mobile-friendly redirect
        if (isMobile) {
          // For mobile devices, use a more reliable redirect method
          setTimeout(() => {
            window.location.replace(session.url);
          }, 100);
        } else {
          window.location.href = session.url;
        }
      } else {
        console.error('❌ Session creation failed:', session);
        throw new Error(session.error || 'Fehler beim Erstellen der Zahlungssession');
      }
    } catch (error) {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://centeo-backend.onrender.com';
      console.error('Payment error:', error);
      console.error('Error details:', error.message);
      console.error('API URL used:', apiUrl);
      
      // Show user-friendly error message with more details
      const errorDetails = `
Fehler: ${error.message}
API: ${apiUrl}
Status: ${error.status || 'Unbekannt'}
Zeit: ${new Date().toLocaleTimeString()}

Mögliche Lösungen:
- Verwenden Sie Test-Karte: 4242 4242 4242 4242
- Prüfen Sie Ihre Internetverbindung
- Versuchen Sie es in 1-2 Minuten erneut
      `.trim();
      
      alert(errorDetails);
    } finally {
      setLoading(false);
    }
  };

  // Free mode handler (email-only, no Stripe)
  const handleFreeAccess = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!email) {
      alert('Bitte E-Mail eingeben.');
      return;
    }
    if (!acceptedTerms) {
      alert('Bitte AGB und Datenschutz akzeptieren.');
      return;
    }

    setLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://centeo-backend.onrender.com';
      // Reuse early-access endpoint to persist email lead
      await fetch(`${apiUrl}/api/early-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          region: farmData?.region || null,
          language: lang || 'de'
        })
      }).catch(() => {});

      // Continue directly to results; pass email + farmData
      onPaymentSuccess({ customerEmail: email, farmData, freeMode: true });
    } catch (err) {
      alert('Fehler bei der Registrierung. Bitte später erneut versuchen.');
      console.error('Free access error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 py-12">
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
            💳 {t('payment.title')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 px-2">
            {isFreeMode ? (
              lang === 'de' ? 'Vorübergehend kostenlos – einfach E-Mail angeben und starten.' :
              lang === 'es' ? 'Temporalmente gratis: introduce tu email y empieza.' :
              'Temporaneamente gratuito: inserisci la tua email e inizia.'
            ) : t('payment.subtitle')}
          </p>
        </div>

        {/* Payment Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6 lg:p-8 mb-6">
          {/* Farm Data Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">📋 {t('payment.analysisFor')}:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
              <div>
                <span className="text-gray-500">🌍 {t('payment.country')}:</span>
                <span className="ml-2 font-medium">{farmData?.country || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-500">🚜 {t('payment.farmType')}:</span>
                <span className="ml-2 font-medium">{farmData?.farmType || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-500">🎯 {t('payment.goal')}:</span>
                <span className="ml-2 font-medium">{farmData?.goal || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-500">📍 {t('payment.region')}:</span>
                <span className="ml-2 font-medium">{farmData?.region || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="border-t border-gray-200 pt-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg">{t('payment.serviceName')}</span>
              <span className="text-lg font-semibold">{isFreeMode ? '0,00€' : t('payment.amount')}</span>
            </div>
            <div className="flex justify-between items-center text-xl font-bold border-t pt-4">
              <span>{t('payment.total')}</span>
              <span className="text-green-600">
                {isFreeMode || couponCode === 'CENTEOTEST' ? '0,00€' : t('payment.amount')}
              </span>
            </div>
            {couponCode === 'CENTEOTEST' && (
              <div className="mt-2 text-sm text-green-600 font-medium">
                ✅ Test-Coupon aktiviert - Kostenlos!
              </div>
            )}
          </div>

          {isFreeMode && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">📧 E-Mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={lang === 'de' ? 'ihre@email.de' : lang === 'es' ? 'tu@email.es' : 'la-tua@email.it'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <div className="mt-3 text-sm">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-gray-600">
                    {lang === 'de' ? (
                      <>Ich akzeptiere die <a href="/terms" target="_blank" className="underline">AGB</a> und die <a href="/privacy" target="_blank" className="underline">Datenschutzbestimmungen</a>.</>
                    ) : lang === 'es' ? (
                      <>Acepto los <a href="/terms" target="_blank" className="underline">términos</a> y la <a href="/privacy" target="_blank" className="underline">política de privacidad</a>.</>
                    ) : (
                      <>Accetto i <a href="/terms" target="_blank" className="underline">termini</a> e l'<a href="/privacy" target="_blank" className="underline">informativa sulla privacy</a>.</>
                    )}
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Test Coupon Code Input - Only visible with ?test=true */}
          {isTestMode && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                🧪 Test-Modus aktiviert
              </label>
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="CENTEOTEST für kostenlosen Test"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
              <div className="mt-1 text-xs text-gray-600">
                Geben Sie "CENTEOTEST" ein für einen kostenlosen Test
              </div>
            </div>
          )}

          {/* Benefits */}
          <div className="mb-6">
            <h4 className="font-semibold text-gray-900 mb-3">✅ {t('payment.whatYouGet')}:</h4>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>{t('payment.benefit1')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>{t('payment.benefit2')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>{t('payment.benefit3')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                <span>{t('payment.benefit4')}</span>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <button 
            onClick={isFreeMode ? handleFreeAccess : handlePayment}
            onTouchStart={(e) => {
              // iOS Safari touch fix - prevent ghost clicks
              console.log('🔥 Touch start detected');
              if (isMobile) {
                e.currentTarget.style.backgroundColor = '#15803d';
              }
            }}
            onTouchEnd={(e) => {
              // Additional mobile fix for touch events
              console.log('🔥 Touch end detected');
              e.preventDefault();
              if (isMobile) {
                e.currentTarget.style.backgroundColor = '#16a34a';
                // Trigger payment on touch end for mobile devices
                // Direct call instead of setTimeout to prevent race conditions
                if (isFreeMode) {
                  handleFreeAccess(e);
                } else {
                  handlePayment(e);
                }
              }
            }}
            disabled={loading}
            className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 text-white py-4 px-6 rounded-xl text-lg font-semibold transition-colors duration-200 mb-4 touch-manipulation cursor-pointer"
            style={{ 
              WebkitTapHighlightColor: 'transparent',
              WebkitUserSelect: 'none',
              userSelect: 'none',
              WebkitTouchCallout: 'none'
            }}
            type="button"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                {t('payment.processing')}
              </div>
            ) : (
              isFreeMode ? (lang === 'de' ? '🚀 Kostenlos starten' : lang === 'es' ? '🚀 Empezar gratis' : '🚀 Inizia gratis') : `💳 ${t('payment.buttonText')}`
            )}
          </button>

          {/* Security Info */}
          {!isFreeMode && (
            <div className="text-center text-sm text-gray-500 mb-4">
              {t('payment.securityInfo')}
            </div>
          )}

          {/* Back Button */}
          <div className="text-center">
            <button 
              onClick={onBack}
              className="text-gray-600 hover:text-gray-800 underline text-sm"
            >
              ← {t('payment.backButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentCheckout;
